import type { TableCardFact } from "./tableCardFacts";
import { EPS, minWidthOf, type CardFactsInput, type FactForm, type MeasuredFact } from "./planCardFacts";

/** Видът на кутийката: десктоп на два реда / на един ред, телефон и таблет. */
export type TableCardFitMode = CardFactsInput["mode"];

const widthOf = (element: Element | null | undefined): number =>
  element ? element.getBoundingClientRect().width : 0;

const heightOf = (element: Element | null | undefined): number =>
  element ? element.getBoundingClientRect().height : 0;

/** „12.5px" → 12.5; празно/„normal" (jsdom) → 0. */
const pixelsOf = (value: string): number => parseFloat(value) || 0;

/** Видим ли е елементът (`hidden` на него или на родител — няма кутии). */
const isShown = (element: Element | null): element is HTMLElement =>
  element !== null && element.getClientRects().length > 0;

/** Името на масата не се събира в мястото си (реже се с „…"). */
export const nameIsCut = (name: HTMLElement): boolean =>
  name.scrollHeight > name.clientHeight + 1 || name.scrollWidth > name.clientWidth + 1;

/** На колко реда е цялото име на масата (без отрязването). */
export const nameLines = (name: HTMLElement): number => {
  const lineHeight = pixelsOf(getComputedStyle(name).lineHeight);
  return lineHeight > 0 ? Math.round(name.scrollHeight / lineHeight) : 1;
};

// Вътрешната ширина на кутийката (без рамката и падинга), точно — clientWidth
// закръгля. −EPS: ширините тук са точни, а допускът на планировчика би пуснал
// факт, по-широк с половин пиксел, и той би излязъл от реда.
const innerWidthOf = (card: HTMLElement): number => {
  const style = getComputedStyle(card);
  const width =
    widthOf(card) -
    pixelsOf(style.borderLeftWidth) -
    pixelsOf(style.borderRightWidth) -
    pixelsOf(style.paddingLeft) -
    pixelsOf(style.paddingRight);
  return width > 0 ? width - EPS : 0;
};

/**
 * Мерките на кутийката за планировчика (`planCardFacts`). Чете се в
 * основата — кутийката без информацията (`data-measuring` крие местата ѝ) —
 * и от невидимия слой за мерене (`CardFactsMeasureLayer`): всеки факт във
 * всеки вид, „+N", чертата между фактите, името на един ред и сумата.
 */
export const readCardFactsInput = (
  card: HTMLElement,
  mode: TableCardFitMode,
  facts: readonly TableCardFact[],
): CardFactsInput => {
  const layer = card.querySelector<HTMLElement>("[data-card-measure]");
  const fromLayer = (selector: string) => layer?.querySelector<HTMLElement>(selector) ?? null;
  const factElement = (index: number, form: FactForm) =>
    fromLayer(`[data-measure-fact="${index}"][data-measure-form="${form}"]`);

  const measuredFacts = facts.map((fact, index): MeasuredFact => {
    // Най-тесният допустим вид: името в него (`data-measure-cut`) се реже.
    const minWidthIn = (form: FactForm) => {
      const element = factElement(index, form);
      const name = element?.querySelector("[data-measure-cut]");
      return minWidthOf(widthOf(element), name ? widthOf(name) : null);
    };
    return {
      kind: fact.kind,
      minWidth: { text: minWidthIn("text"), value: minWidthIn("value"), icon: minWidthIn("icon") },
      textWidth: widthOf(factElement(index, "text")),
      textIsName: fact.textIsName,
    };
  });
  const base = {
    facts: measuredFacts,
    moreWidth: widthOf(fromLayer("[data-measure-more]")),
    dividerWidth: widthOf(fromLayer("[data-measure-divider]")),
    innerWidth: innerWidthOf(card),
    sumWidth: widthOf(fromLayer("[data-measure-sum]")),
  };

  if (mode === "oneLine") {
    // Сервитьорът стои в реда и когато не се събира (`NameIfRoom`, basis 0) —
    // отстоянието до него остава, освен ако е скрит (под 100px).
    const waiterRoom = card.querySelector("[data-waiter-room]")?.parentElement ?? null;
    const lineGap = pixelsOf(getComputedStyle(card).columnGap);
    return {
      ...base,
      mode,
      nameWidth: widthOf(fromLayer("[data-measure-name]")),
      lineGap,
      innerWidth: base.innerWidth - (isShown(waiterRoom) ? lineGap : 0),
    };
  }

  if (mode === "compact") return { ...base, mode };

  const top = card.querySelector<HTMLElement>("[data-card-top]");
  const name = top?.querySelector<HTMLElement>("[data-card-name]") ?? null;
  return {
    ...base,
    mode,
    topHeight: heightOf(top),
    rowHeight: heightOf(factElement(0, "icon")),
    nameLineHeight: name ? pixelsOf(getComputedStyle(name).lineHeight) : 0,
    nameLines: name ? nameLines(name) : 1,
    nameBaseLines: Number(top?.style.getPropertyValue("--name-lines")) || 1,
    nameIsCut: name ? nameIsCut(name) : false,
  };
};
